import { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Tooltip, Switch } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { updateSalesTimeline, setInstallationConfirmed, setSiteVisitArranging, setSiteVisitFlag, getSalesDocuments } from '../../api/salesApi';
import { openWhatsApp } from '../../utils/phoneUtils';
import { buildMilestoneMsg, canMessage, hasMessage, jobFromSale, MILESTONE_MESSAGES } from './scheduleMessage';
import { useUser } from '../../context/userContext';

const PP = "'Poppins', sans-serif";

// ISO datetime -> 'YYYY-MM-DD' for <input type=date>; '' when empty.
const toDateInput = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    // local date parts so the picker shows the stored day, not a TZ-shifted one
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};
// 'YYYY-MM-DD' -> ISO datetime (noon, to dodge TZ roll-over) or null.
const toIso = (s) => (s ? new Date(`${s}T12:00:00`).toISOString() : null);
// RM with cents only when there are any: "RM 5,000" reads better than "RM 5,000.00",
// but "RM 16,826.60" must not round to 16,827.
const money = (v) => {
    const n = Number(v) || 0;
    return 'RM ' + n.toLocaleString('en-MY', {
        minimumFractionDigits: n % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    });
};
const fmt = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Stage order. `edit` = admin/group-admin sets it manually; the two auto stages
// (Payment 1 = deposit date, Completed = on completion) are read-only here.
const BASE_STAGES = [
    { key: 'depositOn', label: 'Deposit', edit: false, note: 'deposit date' },
    // Loan stage, for BANK LOAN sales only. It sits here because the bank has to pay
    // before anything else moves. One date, not two — "submitted to the bank" was a
    // second box nobody was going to key. (user 2026-10-11: "loan application will be
    // ok. remove loan submitted to bank")
    { key: 'loanApplicationOn', label: 'Loan Application', edit: true, loan: true },
    { key: 'sedaSubmittedOn', label: 'SEDA Docs Submitted', edit: true },
    { key: 'sedaApprovedOn', label: 'SEDA Approval', edit: true },
    { key: 'siteVisitOn', label: 'Site Visit', edit: true },
    { key: 'payment', label: 'Payment', edit: false, payment: true },
    // Delivery comes before the crew does, and it is the date a customer actually asks
    // about - the SOP's step 8 opens by confirming it with them. (user 2026-10-04)
    { key: 'deliveryOn', label: 'Product Delivery', edit: true },
    { key: 'installationOn', label: 'Installation', edit: true },
    { key: 'tnbApprovedOn', label: 'TNB Approval', edit: true },
    { key: 'completedOn', label: 'Completed', edit: false, note: 'on completion' },
];
// A loan sale is any sale whose payment method mentions a loan. Matched on the string
// rather than an exact 'LOAN' because historical rows were written by three different
// screens in three different casings.
const isLoanSale = (sale) => /loan/i.test(sale?.paymentMethod || '');
const stagesFor = (sale) => BASE_STAGES.filter(s => !s.loan || isLoanSale(sale));
// Every editable key, loan ones included, so the date state and the save payload do not
// depend on which stages happen to be visible.
const EDIT_KEYS = BASE_STAGES.filter(s => s.edit).map(s => s.key);

export default function ProjectTimeline({ sale, canEdit }) {
    const { user } = useUser();
    const [dates, setDates] = useState(() =>
        EDIT_KEYS.reduce((acc, k) => ({ ...acc, [k]: toDateInput(sale?.[k]) }), {}));
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);

    // Installation confirmation — the same Tentative/Confirmed state the All Scheduling
    // board shows, read and written through the same endpoint. A date agreed by the
    // engineer but not yet by the customer is tentative wherever it is read; a sale page
    // that showed a bare date would quietly contradict the board. (user 2026-10-06)
    const [confirmedAt, setConfirmedAt] = useState(sale?.installationConfirmedOn ?? null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const toggleConfirmed = async (next) => {
        setConfirmBusy(true);
        try {
            const res = await setInstallationConfirmed(sale.salesId, next);
            setConfirmedAt(res?.data?.installationConfirmedOn ?? null);
        } catch { /* leave the switch where it was — the server is the truth */ }
        finally { setConfirmBusy(false); }
    };

    // Site visit "arranging" — the engineer is working out their schedule and will
    // message the customer. The one milestone with nothing to key while it is happening,
    // so a blank box here reads as "nobody has started" when somebody has.
    // (user 2026-10-06: "site visit we dont have date")
    const [arrangingAt, setArrangingAt] = useState(sale?.siteVisitArrangingOn ?? null);
    const [arrangeBusy, setArrangeBusy] = useState(false);
    const toggleArranging = async (next) => {
        setArrangeBusy(true);
        try {
            const res = await setSiteVisitArranging(sale.salesId, next);
            setArrangingAt(res?.data?.siteVisitArrangingOn ?? null);
        } catch { /* leave the switch where it was — the server is the truth */ }
        finally { setArrangeBusy(false); }
    };

    // The Site Visit block. The three ANSWERS are given by the agent at the sale; the two
    // follow-ups are the work the office then has to do, and they are what fails — a crew
    // turned away at a guardhouse that never got the plates. Each toggle is its own PUT, so
    // nothing here waits on Save Timeline. (user 2026-10-11, SOP v61 5.10 / 5.11)
    const [visit, setVisit] = useState({
        gated: sale?.siteGatedGuardedOn ?? null,
        plates: sale?.guardhousePlatesSentOn ?? null,
        needapproval: sale?.mgmtApprovalNeededOn ?? null,
        mgmt: sale?.mgmtApprovalAppliedOn ?? null,
        required: sale?.siteVisitRequired !== false,
    });
    const [visitBusy, setVisitBusy] = useState('');
    const toggleVisit = async (field, next) => {
        setVisitBusy(field);
        try {
            // The endpoint's own name for the one non-date flag; everything else matches.
            const apiField = field === 'required' ? 'sitevisitrequired' : field;
            const d = (await setSiteVisitFlag(sale.salesId, apiField, next))?.data || {};
            setVisit({
                gated: d.siteGatedGuardedOn ?? null,
                plates: d.guardhousePlatesSentOn ?? null,
                needapproval: d.mgmtApprovalNeededOn ?? null,
                mgmt: d.mgmtApprovalAppliedOn ?? null,
                required: d.siteVisitRequired !== false,
            });
        } catch { /* leave the switch where it was — the server is the truth */ }
        finally { setVisitBusy(''); }
    };

    const setField = (k, v) => {
        setDates(p => ({ ...p, [k]: v }));
        setSaved(false);
        // Moving the installation date drops the confirmation, because what was agreed was
        // the OLD date. The server does this on save; the switch follows immediately so the
        // page never claims a confirmation the save is about to clear.
        if (k === 'installationOn' && v !== toDateInput(sale?.installationOn)) setConfirmedAt(null);
        // A real date supersedes "still arranging" — the server clears it on save.
        if (k === 'siteVisitOn' && v) setArrangingAt(null);
    };

    // The slips behind the Payment node. The bar has always shown a percentage with no
    // way to see what made it — this lists each payment with its date and amount.
    //
    // Fetched here rather than threaded down from the page: the documents panel keeps them
    // in its own state, and lifting that up would couple two panels that otherwise share
    // nothing. Re-runs when paidTotal moves, which is exactly when a payment was added,
    // removed or edited (the panel calls fetchSale on every change).
    const [payDocs, setPayDocs] = useState([]);
    useEffect(() => {
        let gone = false;
        if (!sale?.salesId) return undefined;
        getSalesDocuments(sale.salesId)
            .then(res => {
                if (gone) return;
                const rows = (res?.data || [])
                    .filter(d => d.category === 'Deposit' || d.category === 'Payment')
                    // DocDate is the date the customer actually paid; UploadedOn is when the
                    // slip reached us. The first is what the timeline is about, so it wins,
                    // and the row says which one it is showing.
                    .map(d => ({
                        id: d.salesDocumentId,
                        label: d.paymentLabel || d.title || d.category,
                        amount: Number(d.amount) || 0,
                        on: d.docDate || d.uploadedOn,
                        dated: !!d.docDate,
                    }))
                    .sort((a, b) => new Date(a.on) - new Date(b.on));
                setPayDocs(rows);
            })
            .catch(() => { if (!gone) setPayDocs([]); });
        return () => { gone = true; };
    }, [sale?.salesId, sale?.paidTotal]);

    // Payment progress (from the sale's payment-doc totals) drives the Payment node.
    //
    // The denominator is EffectiveTotal — the customer-facing total, which prefers the
    // stored QuotationTotal whenever it is sane. NOT FinalPrice + AddOnPrice, which is what
    // this card used to do and which is wrong on every credit-card sale: FinalPrice is the
    // CC price while AddOnPrice still holds the CASH add-on, so the two halves come off
    // different price lists. Sale #2144 showed RM 53,316 against a card reading RM 54,520 —
    // exactly 38,516 (CC) + 14,800 (cash add-on) instead of + 16,004 (CC add-on).
    // (user 2026-10-05, via Kiki; sales 2079 / 2144 / 2146)
    //
    // Everything else already reads EffectiveTotal — the sales-list bar, the claim state,
    // the Scheduling board — so this card was the last place telling a different story
    // about the same sale. totalPrice stays as the fallback for a response that predates it.
    const paid = Number(sale?.paidTotal) || 0;
    const total = Number(sale?.effectiveTotal) || Number(sale?.totalPrice) || 0;
    const payPct = total > 0 ? Math.round((paid / total) * 100) : 0;
    // isFullyPaid is computed on the server from that same raw sum, so it inherits the bug;
    // the comparison below is the one that decides.
    const fullyPaid = total > 0 && paid >= total;

    // Done-state value: payment node = fully paid; manual stages read local input;
    // other auto stages read the sale.
    // "Not required" is an ANSWER, so the node goes green and the blink moves on. A blank
    // Site Visit otherwise reads as "nobody has booked it" forever on the sales that never
    // needed one. (user 2026-10-11)
    const valueOf = (s) => {
        if (s.payment) return fullyPaid;
        if (s.key === 'siteVisitOn' && !visit.required) return true;
        return s.edit ? dates[s.key] : sale?.[s.key];
    };

    // The job as the customer will read it back: built from what has been SAVED, never
    // from what is sitting unsaved in the boxes, so a message cannot promise a date
    // nobody recorded.
    const job = jobFromSale(sale);
    const sender = user?.firstName || user?.username;

    // Why a milestone's Share is off, said in its tooltip rather than as a line of grey
    // text under the card.
    const waReason = (key) => {
        if (!job.phone) return 'No phone number on this customer';
        if (!job[MILESTONE_MESSAGES[key].needs]) return `Save the ${MILESTONE_MESSAGES[key].label.toLowerCase()} date first`;
        return '';
    };
    const shareMilestone = (key) => openWhatsApp(job.phone, buildMilestoneMsg(key, job, sender));

    // One row of the Site Visit panel. `nested` indents the follow-up under the answer it
    // belongs to, so "plates sent" can never be read as a question of its own.
    const visitRow = (field, on, label, hint, nested) => (
        <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: nested ? 2.5 : 0, py: 0.25 }}>
            <Switch size="small" checked={on} disabled={!canEdit || visitBusy === field}
                onChange={(e) => toggleVisit(field, e.target.checked)} />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, color: on ? '#0F172A' : '#64748B' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#94A3B8' }}>{hint}</Typography>
            </Box>
        </Box>
    );

    const save = async () => {
        setBusy(true);
        try {
            await updateSalesTimeline({
                salesId: sale.salesId,
                sedaSubmittedOn: toIso(dates.sedaSubmittedOn),
                sedaApprovedOn: toIso(dates.sedaApprovedOn),
                siteVisitOn: toIso(dates.siteVisitOn),
                loanApplicationOn: toIso(dates.loanApplicationOn),
                deliveryOn: toIso(dates.deliveryOn),
                installationOn: toIso(dates.installationOn),
                tnbApprovedOn: toIso(dates.tnbApprovedOn),
            });
            setSaved(true);
        } catch {
            alert('Failed to save the timeline. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // The "current" stage = the one right AFTER the furthest-completed stage, so the
    // blink tracks real progress even when later stages were dated before earlier ones
    // (e.g. Installation filled while SEDA is still blank). Only this node blinks; the
    // other still-empty stages stay plain. -1 when everything is done (no blink).
    const stages = stagesFor(sale);
    let lastDoneIdx = -1;
    stages.forEach((s, i) => { if (valueOf(s)) lastDoneIdx = i; });
    const currentIdx = (lastDoneIdx + 1 < stages.length) ? lastDoneIdx + 1 : -1;

    return (
        <Box>
            <style>{`@keyframes tlPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(26,140,255,0.55); } 50% { box-shadow: 0 0 0 5px rgba(26,140,255,0); } }`}</style>
            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.9rem', mb: 1.5 }}>
                Project Timeline
            </Typography>
            <Box>
                {stages.map((s, i) => {
                    const done = !!valueOf(s);
                    const isCurrent = i === currentIdx;
                    const last = i === stages.length - 1;
                    return (
                        <Box key={s.key} sx={{ display: 'flex', gap: 1.25 }}>
                            {/* node + connector line */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{
                                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0, mt: '3px',
                                    bgcolor: done ? '#16A34A' : isCurrent ? '#1A8CFF' : '#fff',
                                    border: `2px solid ${done ? '#16A34A' : isCurrent ? '#1A8CFF' : '#CBD5E1'}`,
                                    animation: isCurrent ? 'tlPulse 1.4s ease-in-out infinite' : 'none',
                                }} />
                                {!last && <Box sx={{ width: 2, flex: 1, minHeight: s.edit && canEdit ? 28 : 16, bgcolor: done ? '#86EFAC' : '#E2E8F0' }} />}
                            </Box>
                            {/* label + date / input */}
                            <Box sx={{ pb: 1.5, flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: isCurrent ? 700 : 600, color: done ? '#0F172A' : isCurrent ? '#1A8CFF' : '#64748B' }}>
                                    {s.label}
                                    {!s.edit && s.note && (
                                        <Typography component="span" sx={{ fontSize: '0.62rem', color: '#94A3B8', ml: 0.5, fontWeight: 400 }}>
                                            · auto ({s.note})
                                        </Typography>
                                    )}
                                    {s.payment && (
                                        <Typography component="span" sx={{ fontSize: '0.62rem', color: '#94A3B8', ml: 0.5, fontWeight: 400 }}>
                                            · from payment docs
                                        </Typography>
                                    )}
                                </Typography>
                                {s.payment ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 600, color: fullyPaid ? '#16A34A' : '#475569' }}>
                                            {/* money(), not toLocaleString(): the default drops a
                                                trailing zero, so RM 16,826.60 printed as
                                                "RM 16,826.6" — and now sits directly above the
                                                slips that add up to it. */}
                                            {money(paid)} / {money(total)} · {payPct}%
                                        </Typography>
                                        <Box sx={{ height: 5, mt: 0.4, borderRadius: 3, bgcolor: '#F1F5F9', overflow: 'hidden' }}>
                                            <Box sx={{ height: '100%', width: `${Math.min(100, payPct)}%`, bgcolor: fullyPaid ? '#16A34A' : '#1A8CFF', borderRadius: 3, transition: 'width 0.6s ease' }} />
                                        </Box>
                                        {/* What made that percentage. One row per slip, oldest
                                            first, so the payment history reads down the page the
                                            same way the milestones above it do. */}
                                        {payDocs.length > 0 && (
                                            <Box sx={{ mt: 0.75 }}>
                                                {payDocs.map(d => (
                                                    <Box key={d.id} sx={{
                                                        display: 'flex', alignItems: 'baseline', gap: 1,
                                                        py: 0.2, borderTop: '1px dashed #E2E8F0',
                                                    }}>
                                                        <Typography sx={{
                                                            fontFamily: PP, fontSize: '0.72rem', color: '#475569',
                                                            flex: 1, minWidth: 0, overflow: 'hidden',
                                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {d.label}
                                                        </Typography>
                                                        <Tooltip title={d.dated
                                                            ? 'Date the customer paid'
                                                            : 'No payment date was keyed — showing when the slip was uploaded'}>
                                                            <Typography sx={{
                                                                fontFamily: PP, fontSize: '0.7rem',
                                                                color: d.dated ? '#64748B' : '#B45309',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                {fmt(d.on)}{d.dated ? '' : ' · uploaded'}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Typography sx={{
                                                            fontFamily: PP, fontSize: '0.72rem', fontWeight: 700,
                                                            color: '#0F172A', whiteSpace: 'nowrap',
                                                        }}>
                                                            {money(d.amount)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    /* Each milestone that has a preset message carries its own
                                       Share, beside its own date — the moment a date is keyed is
                                       the moment the customer should hear about it.
                                       (user 2026-10-05: "the share whatsapp shd be beside those
                                       milestone that has watsapp message") */
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        {s.edit && canEdit ? (
                                            <TextField
                                                type="date" size="small" value={dates[s.key] || ''}
                                                onChange={(e) => setField(s.key, e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ mt: 0.5, '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.76rem', borderRadius: '8px' } }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: done ? '#16A34A' : '#94A3B8', mt: 0.25 }}>
                                                {fmt(valueOf(s))}
                                            </Typography>
                                        )}
                                        {hasMessage(s.key) && (
                                            <Tooltip title={waReason(s.key)
                                                || `Opens WhatsApp with the ${MILESTONE_MESSAGES[s.key].label.toLowerCase()} details written out \u2014 you still press send`}>
                                                <span>
                                                    <Button
                                                        size="small" variant="outlined"
                                                        disabled={!canMessage(s.key, job)}
                                                        onClick={() => shareMilestone(s.key)}
                                                        startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                                                        sx={{
                                                            mt: s.edit && canEdit ? 0.5 : 0, py: 0.4,
                                                            fontFamily: PP, textTransform: 'none', fontWeight: 600,
                                                            fontSize: '0.72rem', borderRadius: '8px',
                                                            color: '#128C7E', borderColor: '#A7D8CD',
                                                            '&:hover': { borderColor: '#128C7E', bgcolor: '#F0FDF9' },
                                                        }}
                                                    >
                                                        Share
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {/* Still arranging — the API only sends this property once it has the column, so an older
                                            // API leaves it `undefined` and the switch stays hidden —
                                            // the control appears by itself when the backend is ready,
                                            // and can never be shown with nowhere to save to. */}
                                        {s.key === 'siteVisitOn' && !dates.siteVisitOn
                                            && sale?.siteVisitArrangingOn !== undefined && (
                                            <Box sx={{
                                                width: '100%', mt: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                                                px: 1.25, py: 0.75, borderRadius: '10px',
                                                bgcolor: arrangingAt ? '#FFFBEB' : '#F8FAFC',
                                                border: `1px solid ${arrangingAt ? '#FDE68A' : '#E2E8F0'}`,
                                            }}>
                                                <Switch
                                                    size="small"
                                                    checked={!!arrangingAt}
                                                    disabled={!canEdit || arrangeBusy}
                                                    onChange={(e) => toggleArranging(e.target.checked)}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{
                                                        fontFamily: PP, fontSize: '0.78rem', fontWeight: 700,
                                                        color: arrangingAt ? '#B45309' : '#475569',
                                                    }}>
                                                        {arrangingAt ? 'Arranging' : 'Not arranged yet'}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                        {arrangingAt
                                                            ? 'Engineer is fixing a slot and will message the customer'
                                                            : 'Switch on while the engineer works out a slot'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* ── Site Visit ───────────────────────────────────────
                                            The agent answered "gated" and "needs approval" at the
                                            sale; what is tracked here is the WORK those answers
                                            create — plates to the guardhouse, a form to management —
                                            because that is the part that fails and turns a crew away
                                            at the gate on installation morning. Each switch is its
                                            own PUT, so none of this waits on Save Timeline.
                                            `siteVisitRequired === undefined` means an API that
                                            predates the columns, and the panel stays hidden.
                                            (user 2026-10-11, SOP v61 5.10 / 5.11) */}
                                        {s.key === 'siteVisitOn' && sale?.siteVisitRequired !== undefined && (
                                            <Box sx={{ width: '100%', mt: 0.75, px: 1.25, py: 0.75, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                                {visitRow('required', visit.required, 'Site visit required',
                                                    visit.required ? 'Switch off if this job needs no visit' : 'Marked not required — the stage counts as settled')}
                                                {visitRow('gated', !!visit.gated, 'Gated & guarded',
                                                    visit.gated ? `Answered ${fmt(visit.gated)}` : 'No guardhouse to clear')}
                                                {!!visit.gated && visitRow('plates', !!visit.plates, 'Car plates sent to guardhouse',
                                                    visit.plates ? `Sent ${fmt(visit.plates)}` : 'Send 1–2 days before installation', true)}
                                                {visitRow('needapproval', !!visit.needapproval, 'Needs management approval',
                                                    visit.needapproval ? `Answered ${fmt(visit.needapproval)}` : 'No management form needed')}
                                                {!!visit.needapproval && visitRow('mgmt', !!visit.mgmt, 'Management form submitted',
                                                    visit.mgmt ? `Submitted ${fmt(visit.mgmt)}` : 'Customer gets the form; we fill in our details and apply', true)}
                                            </Box>
                                        )}

                                        {/* Confirmed / Tentative, directly under the installation
                                            date — same wording and same colours as the board. */}
                                        {s.key === 'installationOn' && (
                                            <Box sx={{
                                                width: '100%', mt: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                                                px: 1.25, py: 0.75, borderRadius: '10px',
                                                bgcolor: confirmedAt ? '#F0FDF4' : '#F8FAFC',
                                                border: `1px solid ${confirmedAt ? '#BBF7D0' : '#E2E8F0'}`,
                                            }}>
                                                <Switch
                                                    size="small"
                                                    checked={!!confirmedAt}
                                                    disabled={!canEdit || !dates.installationOn || confirmBusy}
                                                    onChange={(e) => toggleConfirmed(e.target.checked)}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{
                                                        fontFamily: PP, fontSize: '0.78rem', fontWeight: 700,
                                                        color: confirmedAt ? '#166534' : '#475569',
                                                    }}>
                                                        {confirmedAt
                                                            ? 'Installation confirmed by the customer'
                                                            : 'Installation not confirmed yet — tentative'}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                        {!dates.installationOn
                                                            ? 'Key an installation date first'
                                                            : confirmedAt
                                                                ? `Marked ${fmt(confirmedAt)} · changing the date clears this`
                                                                : 'The customer has not replied OK yet'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {canEdit && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                        size="small" variant="contained" disabled={busy} onClick={save}
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', boxShadow: 'none' }}
                    >
                        {busy ? 'Saving…' : saved ? 'Saved ✓' : 'Save Timeline'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
